import * as remoteAction from "../remoteAction";
import get from "lodash/get";
import omit from "lodash/omit";
import { store } from "../../store";
import { StoreState } from "../../reducers";
import {
  getCampaignId,
  getMerchantId,
  getScreenName,
  getWidgetConfig,
  getWidgetConfigId,
  getWidgetData,
} from "../../reducers/methods";
import { getLocaleFromPathname, getQueryVariable } from "../../utils/url";

export type TrackEventActionSuccess = remoteAction.MerchantRemoteResponse<
  "EVENT_SUCCESS",
  any
>;
export type TrackEventActionFailure = remoteAction.MerchantRemoteResponse<
  "EVENT_FAILURE",
  any
>;
export type TrackEventActionRequest = remoteAction.MerchantRemoteResponse<
  "EVENT_REQUEST",
  any
>;

export interface TrackEventAction extends remoteAction.PostRemoteRequest {
  types: {
    request: "EVENT_REQUEST";
    success: "EVENT_SUCCESS";
    failure: "EVENT_FAILURE";
  };
}

export interface TrackEventParams {
  type: string;
  // tslint:disable-next-line:no-any
  [prop: string]: any;
}

// tslint:disable-next-line: cyclomatic-complexity
export const trackEvent = ({
  type,
  ...payload
}: TrackEventParams): TrackEventAction => {
  const state = store.getState() as StoreState;
  const screenName = get(
    getWidgetConfig(state)[getScreenName(state)],
    "label",
    "N/A"
  );
  const customerEmail = get(getWidgetData(state), "customer.email");
  const customerName = get(getWidgetData(state), "customer.name");
  // ENH-388: stamped from widget data the same way `name` is, so a widget that
  // collects first/last name persists them without every widget config having
  // to add `firstName: "{{customer.firstName}}"` to its trackEvent args.
  // getWidgetData is defaultsDeep(userInput, merchant, config), so a form input
  // named `customer.firstName` wins over a merchant `track customer` value.
  const customerFirstName = get(getWidgetData(state), "customer.firstName");
  const customerLastName = get(getWidgetData(state), "customer.lastName");

  const referralCode = getQueryVariable("referralCode") || getQueryVariable("fbuy_ref_code");
  const productVariantId = getQueryVariable("fbProductVariantId");

  // Explicit action args win over the value stamped from widget data. Resolved
  // ONCE, before basePayload, because basePayload is spread into both
  // `data.payload` and the outer `data` envelope while `...payload` overrides
  // only the inner one -- so resolving late would let the two carry different
  // names for the same customer. Same resolve-once treatment campaignId /
  // merchantId / widgetConfigId already get below.
  //
  // Pulled OUT of the residual spread as well: `payload` is spread into the
  // inner payload below, so an explicit falsy arg (`firstName: ""`, `null`)
  // would override the resolved value there while the outer envelope kept it —
  // the same divergence, just through the falsy door.
  const {
    firstName: firstNameArg,
    lastName: lastNameArg,
    ...restPayload
  } = payload;

  const firstName = firstNameArg || customerFirstName;
  const lastName = lastNameArg || customerLastName;

  // ENH-359: the host page's locale, from the URL the merchant SDK hands the
  // widget (`url.pathname`).  Email captures only: that is the one event it is
  // forwarded from, and every other event type is left exactly as it was.
  // Guarded like the names below, because an empty string fails the track
  // payload schema and a schema failure discards the whole event.
  //
  // A caller-supplied `pageLocale` is dropped from the payload rather than
  // allowed to override: it describes the page, not the widget config, and
  // `restPayload` is spread into the inner payload only, so an override would
  // leave the two carrying different locales (or an empty one past the guard).
  const eventType = type || payload.name;
  const isEmailCapture = eventType === "email_capture";
  const pageLocale = isEmailCapture
    ? getLocaleFromPathname(get(getWidgetData(state), "url.pathname"))
    : undefined;

  const basePayload = {
    customerEmail: customerEmail || "",
    campaignId: payload.campaignId || getCampaignId(state),
    merchantId: payload.merchantId || getMerchantId(state),
    screenId: getScreenName(state),
    widgetConfigId: payload.widgetConfigId || getWidgetConfigId(state),
    ...(referralCode && { referralCode }),
    ...(productVariantId && { productVariantId }),
    ...(customerName && { name: customerName }),
    // Guarded, not `|| ""`: before these keys were declared in the track payload
    // schemas they fell under `additionalProperties`, where an empty string
    // failed `minLength: 1` and a schema failure discards the WHOLE event.  The
    // schemas now permit `""`/null, so this is belt-and-braces — but sending a
    // value we don't have is still noise.
    ...(firstName && { firstName }),
    ...(lastName && { lastName }),
    ...(pageLocale && { pageLocale }),
  };

  return {
    type: "MERCHANT_REMOTE_REQUEST",
    types: {
      request: "EVENT_REQUEST",
      failure: "EVENT_FAILURE",
      success: "EVENT_SUCCESS",
    },
    method: "POST",
    https: true,
    data: {
      type: eventType,
      payload: {
        screenName,
        ...basePayload,
        ...(isEmailCapture ? omit(restPayload, "pageLocale") : restPayload),
      },
      ...basePayload,
    },
    url: `/events/${payload.merchantId || getMerchantId(state)}/track`,
  };
};
